import { AUDIT_ACTION_FILTER_PATTERN, LABEL_CHANGES, RETENTION_CATEGORIES } from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, isoDaySchema, reasonSchema } from './clinical-common.ts';
import { pageQuerySchema } from './pagination.ts';
import { sensitivityLabelInputSchema } from './registration-settings.ts';

// Privacy tooling (docs/plans/golive-privacy-tooling.md): sensitivity label management and
// visit labels, the break-glass report, the audit viewer's filters and the retention
// schedule. Reasons and review notes are PHI: bodies only, never URLs or logs.

export { LABEL_CHANGES, RETENTION_CATEGORIES };
export type { LabelChange, RetentionCategory } from '@wodi/domain';

// --- Sensitivity labels (§1) ----------------------------------------------------------------

const roleRefSchema = z.object({ id: z.uuid(), name: z.string() });

export const labelSettingsLabelSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  display: z.string(),
  description: z.string().nullable(),
  status: z.enum(['active', 'inactive']),
  /** The roles cleared for it. */
  roles: z.array(roleRefSchema),
});
export type LabelSettingsLabel = z.infer<typeof labelSettingsLabelSchema>;

export const labelSettingsSchema = z.object({
  labels: z.array(labelSettingsLabelSchema),
  /** Every role of the organisation, to choose from. */
  roles: z.array(roleRefSchema),
});
export type LabelSettingsView = z.infer<typeof labelSettingsSchema>;

const descriptionSchema = z.string().trim().max(500);

export const createLabelBodySchema = sensitivityLabelInputSchema.extend({
  description: descriptionSchema.optional(),
});
export type CreateLabelBody = z.infer<typeof createLabelBodySchema>;

/** The code never changes; the name and description can be corrected; switched off, never deleted. */
export const updateLabelBodySchema = z.object({
  display: z.string().trim().min(1).max(80),
  description: descriptionSchema.nullable(),
  status: z.enum(['active', 'inactive']),
});
export type UpdateLabelBody = z.infer<typeof updateLabelBodySchema>;

export const labelRolesBodySchema = z.object({ roleIds: z.array(z.uuid()).max(100) });
export type LabelRolesBody = z.infer<typeof labelRolesBodySchema>;

const labelRefSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  display: z.string(),
  description: z.string().nullable(),
});
export type LabelRef = z.infer<typeof labelRefSchema>;

export const visitSensitivitySchema = z.object({
  visitId: z.uuid(),
  labels: z.array(labelRefSchema),
  /** Labels in use the reader's roles are cleared for and the visit doesn't carry. */
  available: z.array(labelRefSchema),
  history: z.array(
    z.object({
      id: z.uuid(),
      label: z.object({ id: z.uuid(), display: z.string() }),
      change: z.enum(['applied', 'removed']),
      reason: z.string(),
      at: z.iso.datetime(),
      by: actorSchema,
    }),
  ),
});
export type VisitSensitivityView = z.infer<typeof visitSensitivitySchema>;

/** Put on (`…/sensitivity/apply`) or taken off (`…/sensitivity/remove`), with a reason. */
export const visitLabelChangeBodySchema = z.object({
  labelId: z.uuid(),
  reason: reasonSchema,
});
export type VisitLabelChangeBody = z.infer<typeof visitLabelChangeBodySchema>;

// --- Break-glass report (§2) ----------------------------------------------------------------

export const breakGlassQuerySchema = pageQuerySchema.extend({
  state: z.enum(['unreviewed', 'all']).default('unreviewed'),
});

export const breakGlassEntrySchema = z.object({
  /** The `record.break_glass` audit event. */
  id: z.uuid(),
  occurredAt: z.iso.datetime(),
  actor: actorSchema,
  facility: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  visit: z.object({ id: z.uuid(), visitNumber: z.string() }).nullable(),
  reason: z.string().nullable(),
  review: z.object({ by: actorSchema, at: z.iso.datetime(), note: z.string() }).nullable(),
});
export type BreakGlassEntry = z.infer<typeof breakGlassEntrySchema>;

export const breakGlassPageSchema = z.object({
  items: z.array(breakGlassEntrySchema),
  nextCursor: z.uuid().nullable(),
  /** How many openings nobody has reviewed yet, in all. */
  unreviewed: z.number().int(),
});
export type BreakGlassPage = z.infer<typeof breakGlassPageSchema>;

export const breakGlassReviewBodySchema = z.object({
  note: z.string().trim().min(5).max(2000),
});
export type BreakGlassReviewBody = z.infer<typeof breakGlassReviewBodySchema>;

// --- Audit viewer (§3) ----------------------------------------------------------------------

export const auditQuerySchema = pageQuerySchema.extend({
  entityType: z.string().max(64).optional(),
  /** The record: an audit event's entity ID. */
  entityId: z.uuid().optional(),
  actorId: z.uuid().optional(),
  /** An action, or the start of one ending in a dot ("record."). */
  action: z
    .string()
    .trim()
    .max(80)
    .regex(AUDIT_ACTION_FILTER_PATTERN, 'Lower-case words joined by dots, like record.break_glass')
    .optional(),
  /** East Africa days, inclusive. */
  from: isoDaySchema.optional(),
  to: isoDaySchema.optional(),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;

// --- Retention (§4) -------------------------------------------------------------------------

export const retentionScheduleSchema = z.object({
  rules: z.array(
    z.object({
      category: z.enum(RETENTION_CATEGORIES),
      description: z.string(),
      keepDays: z.number().int(),
      defaultDays: z.number().int(),
      minDays: z.number().int(),
      maxDays: z.number().int(),
      isDefault: z.boolean(),
      provisional: z.boolean(),
    }),
  ),
  fixed: z.array(z.object({ record: z.string(), kept: z.string() })),
  /** The latest purge run in this organisation: when, and counts per category. */
  lastRun: z
    .object({ at: z.iso.datetime(), counts: z.record(z.string(), z.number().int()) })
    .nullable(),
});
export type RetentionScheduleView = z.infer<typeof retentionScheduleSchema>;

export const retentionRuleBodySchema = z.object({
  keepDays: z.number().int().min(1).max(3650),
});
export type RetentionRuleBody = z.infer<typeof retentionRuleBodySchema>;
